import { useState } from "react";
import { Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { DropdownSelect, IOptions } from "components/DropdownSelect";
import { CustomInputModal } from "components/CustomInputModal";
import { CustomDatePicker } from "components/DatePicker";
import { MaisFiltros } from "components/ButtonModal";
import { IFiltroDebitoIpva } from "../../interfaces/IFiltroDebitoIpva";
import {
  AcoesContainer,
  Button,
  ButtonsContainer,
  Container,
  DatePickerContainer,
  FormContainer,
  InputContainer,
  InputContainerMaisFiltros,
  Title,
} from "./style";
import { StatusPagamento } from "features/core/pages/Financeiro/Enum/StatusPagamento";

interface IProps {
  modo: "aguardando" | "pago";
  empresasOptions: IOptions[];
  divisoesOptions: IOptions[];
  empresaSelecionada: number | undefined;
  onChangeEmpresa: (valor: number) => void;
  onSubmit: (filtros: IFiltroDebitoIpva) => void;
  validarVeiculos: (veiculos: string[]) => Promise<any>;
  validarProcessos: (processos: string[]) => Promise<any>;
}

const statusProcessoOptions: IOptions[] = [
  { label: "Pré cadastro", value: 0 },
  { label: "Em andamento", value: 1 },
  { label: "Pendente", value: 2 },
  { label: "Finalizado", value: 3 },
  { label: "Cancelado", value: 4 },
];

const statusOptionsPago: IOptions[] = [
  { label: "Pago", value: StatusPagamento.Pago },
  { label: "Pagamento manual", value: StatusPagamento["Pagamento manual"] },
];

const statusOptionsAguardando: IOptions[] = [
  { label: "Pendente", value: StatusPagamento.Pendente },
  { label: "Processando", value: StatusPagamento.Processando },
  { label: "Erro", value: StatusPagamento.Erro },
];

export function FiltrosPagarIpva({
  modo,
  empresasOptions,
  divisoesOptions,
  empresaSelecionada,
  onChangeEmpresa,
  onSubmit,
  validarVeiculos,
  validarProcessos,
}: IProps) {
  const { handleSubmit, control, setValue, getValues } =
    useForm<IFiltroDebitoIpva>({
      defaultValues: {
        idEmpresa: null,
        idDivisao: null,
        dadosVeiculos: [],
        idsProcessos: [],
        statusPagamento: null,
        statusProcesso: null,
        inicioVencimento: null,
        fimVencimento: null,
        inicioPagamento: null,
        fimPagamento: null,
      },
    });

  const [inicioVencimento, setInicioVencimento] = useState<Date | undefined>();
  const [fimVencimento, setFimVencimento] = useState<Date | undefined>();
  const [inicioPagamento, setInicioPagamento] = useState<Date | undefined>();
  const [fimPagamento, setFimPagamento] = useState<Date | undefined>();

  const handleLimpar = () => {
    setValue("idEmpresa", null);
    setValue("idDivisao", null);
    setValue("dadosVeiculos", []);
    setValue("idsProcessos", []);
    setValue("statusPagamento", null);
    setValue("statusProcesso", null);
    setValue("inicioVencimento", null);
    setValue("fimVencimento", null);
    setValue("inicioPagamento", null);
    setValue("fimPagamento", null);
    setInicioVencimento(undefined);
    setFimVencimento(undefined);
    setInicioPagamento(undefined);
    setFimPagamento(undefined);
    onChangeEmpresa(0 as any);
    onSubmit({});
  };

  const handleLimparMaisFiltros = () => {
    setValue("idDivisao", null);
    setValue("idsProcessos", []);
    setValue("statusProcesso", null);
  };

  const submitForm = (dados: IFiltroDebitoIpva) => {
    onSubmit({
      ...dados,
      inicioVencimento: inicioVencimento ?? null,
      fimVencimento: fimVencimento ?? null,
      inicioPagamento: inicioPagamento ?? null,
      fimPagamento: fimPagamento ?? null,
    });
  };

  return (
    <Container>
      <Title>Filtros</Title>
      <hr />
      <FormContainer>
        <Form noValidate onSubmit={handleSubmit(submitForm)}>
          <Row>
            <InputContainer lg="2">
              <Form.Group>
                <Form.Label>Empresa</Form.Label>
                <Controller
                  control={control}
                  name="idEmpresa"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect
                      options={empresasOptions}
                      title=""
                      selectedValue={value ?? undefined}
                      onSelect={(val) => {
                        onChange(val);
                        onChangeEmpresa(val);
                      }}
                      searchable
                      required
                    />
                  )}
                />
              </Form.Group>
            </InputContainer>

            <InputContainer lg="2">
              <Form.Group>
                <Form.Label>Veículo</Form.Label>
                <Controller
                  control={control}
                  name="dadosVeiculos"
                  defaultValue={[]}
                  render={({ field: { onChange, value } }) => (
                    <CustomInputModal
                      label="Filtrar em massa"
                      value={value ?? []}
                      placeholder="Placa, chassi ou renavam"
                      onConfirm={onChange}
                      onClickOk={validarVeiculos}
                      validar={false}
                      style={{ width: "100%" }}
                      placeholderModal="Digite ou cole placa, chassi ou renavam e tecle ENTER"
                      labelItem="Veículos"
                    />
                  )}
                />
              </Form.Group>
            </InputContainer>

            <InputContainer lg="2">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Controller
                  control={control}
                  name="statusPagamento"
                  render={({ field: { onChange, value } }) => (
                    <DropdownSelect
                      title=""
                      options={
                        modo === "aguardando"
                          ? statusOptionsAguardando
                          : statusOptionsPago
                      }
                      selectedValue={value ?? undefined}
                      onSelect={onChange}
                    />
                  )}
                />
              </Form.Group>
            </InputContainer>

            <InputContainer lg="2">
              <Form.Group>
                {modo === "aguardando" ? (
                  <Controller
                    control={control}
                    name="inicioVencimento"
                    render={() => (
                      <DatePickerContainer>
                        <span>Período de vencimento</span>
                        <CustomDatePicker
                          selected={inicioVencimento}
                          onChange={(dates: any) => {
                            const [start, end] = dates;
                            setInicioVencimento(start);
                            setFimVencimento(end);
                          }}
                          startDate={inicioVencimento}
                          endDate={fimVencimento}
                          rangeSelect={true}
                        />
                      </DatePickerContainer>
                    )}
                  />
                ) : (
                  <Controller
                    control={control}
                    name="inicioPagamento"
                    render={() => (
                      <DatePickerContainer>
                        <span>Período de pagamento</span>
                        <CustomDatePicker
                          selected={inicioPagamento}
                          onChange={(dates: any) => {
                            const [start, end] = dates;
                            setInicioPagamento(start);
                            setFimPagamento(end);
                          }}
                          startDate={inicioPagamento}
                          endDate={fimPagamento}
                          rangeSelect={true}
                        />
                      </DatePickerContainer>
                    )}
                  />
                )}
              </Form.Group>
            </InputContainer>

            <InputContainer lg="4">
              <Form.Group>
                <ButtonsContainer>
                  <AcoesContainer>
                    <Button
                      type="reset"
                      outline
                      variante="primary"
                      style={{ width: "76px" }}
                      onClick={handleLimpar}
                    >
                      Limpar
                    </Button>
                    <Button type="submit" style={{ width: "76px" }}>
                      Buscar
                    </Button>
                  </AcoesContainer>
                  <MaisFiltros
                    handleClickAplicarFiltros={handleSubmit(submitForm)}
                    limparTudoOnClick={handleLimparMaisFiltros}
                    cancelarOnClick={handleLimparMaisFiltros}
                  >
                    <InputContainerMaisFiltros>
                      <Form.Label>Divisão</Form.Label>
                      <Controller
                        control={control}
                        name="idDivisao"
                        render={({ field: { onChange, value } }) => (
                          <DropdownSelect
                            title=""
                            options={divisoesOptions}
                            selectedValue={value ?? undefined}
                            onSelect={onChange}
                            disabled={!empresaSelecionada}
                            searchable
                          />
                        )}
                      />
                    </InputContainerMaisFiltros>

                    <InputContainerMaisFiltros>
                      <Form.Label>Processo</Form.Label>
                      <Controller
                        control={control}
                        name="idsProcessos"
                        defaultValue={[]}
                        render={({ field: { onChange, value } }) => (
                          <CustomInputModal
                            label="Filtrar em massa"
                            value={value ?? []}
                            placeholder="IDs dos processos"
                            onConfirm={onChange}
                            onClickOk={validarProcessos}
                            validar={false}
                            style={{ width: "100%" }}
                            placeholderModal="Digite ou cole os IDs dos processos e tecle ENTER"
                            labelItem="Processo"
                          />
                        )}
                      />
                    </InputContainerMaisFiltros>
                    <InputContainerMaisFiltros>
                      <Form.Label>Status do processo</Form.Label>
                      <Controller
                        control={control}
                        name="statusProcesso"
                        render={({ field: { onChange, value } }) => (
                          <DropdownSelect
                            title=""
                            options={statusProcessoOptions}
                            selectedValue={value ?? undefined}
                            onSelect={onChange}
                          />
                        )}
                      />
                    </InputContainerMaisFiltros>
                  </MaisFiltros>
                </ButtonsContainer>
              </Form.Group>
            </InputContainer>
          </Row>
        </Form>
      </FormContainer>
    </Container>
  );
}
